import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import ReactApexChart from 'react-apexcharts';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import {
  Box,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

// ── helpers ───────────────────────────────────────────────────────────────────

/** "2026-01"  →  "Jan '26" */
const fmtMonth = (iso) => {
  const [yr, mo] = iso.split('-');
  const label = new Date(Number(yr), Number(mo) - 1, 1)
    .toLocaleString('en-IN', { month: 'short' });
  return `${label} '${yr.slice(2)}`;
};

const fmt = (n) => Number(n).toLocaleString('en-IN');

// ── Stat cards removed ──

// ── skeleton ──────────────────────────────────────────────────────────────────
const SkeletonView = () => (
  <Box sx={{ p: '16px 16px 12px' }}>
    <Stack direction="row" justifyContent="space-between" mb={2}>
      <Skeleton variant="text" width={150} height={20} />
      <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: '10px' }} />
    </Stack>
    <Skeleton variant="rounded" height={230} sx={{ borderRadius: '8px' }} />
  </Box>
);

// ── empty / error state (reused for non-Redcliffe too) ────────────────────────
const EmptyState = ({ isError }) => (
  <Stack alignItems="center" justifyContent="center" gap={1.25} py={2} pb={3}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '50%',
          background: isError ? '#fff1f2' : '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          {isError ? (
            <>
              <path d="M12 9v4M12 17h.01" stroke="#e11d48" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#e11d48" strokeWidth="1.5" strokeLinejoin="round" />
            </>
          ) : (
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '13.5px', color: '#475569', mb: 0.3 }}>
          {isError ? 'Failed to load data' : 'No data available.'}
        </Typography>
        <Typography sx={{ fontSize: '11.5px', color: '#94a3b8', maxWidth: 300 }}>
          {isError
            ? 'There was a problem fetching repeat rate data. Please try again.'
            : 'Patient repeat rate metrics will appear here once available.'}
        </Typography>
      </Box>
    </Stack>
);

// ── main component ────────────────────────────────────────────────────────────
const SmartReportRepeatRate = ({ loginData, navBarTriggered, onDateRangeChange }) => {
  const [trendData, setTrendData]   = useState(null);   // array of monthly objects
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(false);
  const [chartMode, setChartMode]   = useState('trend'); // 'trend' | 'bar'
  const chartWrapperRef             = useRef(null);

  // When sidebar toggles, wait for its CSS transition (300ms) to finish,
  // then fire a window resize event so ApexCharts recalculates its width.
  useEffect(() => {
    const timer = setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 350);
    return () => clearTimeout(timer);
  }, [navBarTriggered]);

  // Track selected client center as stable state — updated only when localStorage changes.
  // Using inline read in render body caused isRedcliffe to flip on every parent re-render,
  // triggering fetchTrend() multiple times (4x duplicate calls).
  const [selectedClientCenter, setSelectedClientCenter] = useState(() => {
    try { return (JSON.parse(localStorage.getItem('clientPreference')) || '').toLowerCase(); } catch { return ''; }
  });

  useEffect(() => {
    const handler = () => {
      try {
        const val = (JSON.parse(localStorage.getItem('clientPreference')) || '').toLowerCase();
        setSelectedClientCenter(val);
      } catch { setSelectedClientCenter(''); }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // isRedcliffe is true when:
  //  1. The logged-in user is a Redcliffe lab user (loginData.center === 'redcliffe'), OR
  //  2. An admin has selected a Redcliffe client (clientPreference in localStorage === 'redcliffe')
  const isRedcliffe = (loginData && loginData.center === 'redcliffe') ||
                      selectedClientCenter === 'redcliffe';

  // ── fetch ─────────────────────────────────────────────────────────────────
  const fetchTrend = async () => {
    if (!isRedcliffe) { setLoading(false); return; }

    setLoading(true);
    setError(false);
    try {
      const idToken =
        localStorage.getItem('idTokenBilling') ||
        JSON.parse(localStorage.getItem('loginDetails'))?.idToken || '';
      if (!idToken) { setError(true); setLoading(false); return; }

      const orgPreference      = JSON.parse(localStorage.getItem('orgPreference'))      || '';
      const clientPreference   = JSON.parse(localStorage.getItem('clientPreference'))   || '';
      const clientIdPreference = JSON.parse(localStorage.getItem('clientIdPreference')) || '';
      const hasClientFilter    = orgPreference && clientPreference && clientPreference !== 'none';

      let url = `https://api.niroggyan.com/billing/smartreport/counts?query_by=monthYear&query_on=repeat_rate&ignoreActivityLog=true`;
      if (hasClientFilter) {
        url += `&org=${encodeURIComponent(orgPreference)}&center=${encodeURIComponent(clientPreference)}`;
        if (clientIdPreference) url += `&clientId=${encodeURIComponent(clientIdPreference)}`;
      }
      
      const res = await axios({ method: 'GET', url, headers: { Authorization: idToken } });

      if (res.data.isSuccess && Array.isArray(res.data.data) && res.data.data.length) {
        // Sort ascending by month so the chart reads left → right chronologically
        const sorted = [...res.data.data].sort((a, b) => a.month.localeCompare(b.month));
        setTrendData(sorted);
        if (res.data.metdata?.min_date && onDateRangeChange) {
          onDateRangeChange(res.data.metdata.min_date);
        }
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTrend(); }, [isRedcliffe]);

  // ── derived values ────────────────────────────────────────────────────────
  const months      = trendData ? trendData.map((d) => fmtMonth(d.month)) : [];
  const totalPts    = trendData ? trendData.map((d) => parseInt(d.total_patients))     : [];
  const returningPts= trendData ? trendData.map((d) => parseInt(d.returning_patients)) : [];
  const newPts      = trendData ? trendData.map((d) => parseInt(d.new_patients))       : [];
  const pctArr      = trendData ? trendData.map((d) => parseFloat(d.returning_percentage)) : [];

  // ── ApexCharts config ─────────────────────────────────────────────────────
  // Series shape changes by chartMode:
  //   trend → bar + area + area + line (hybrid)
  //   bar   → plain bar for each of the 3 count series + line for %
  const isTrend = chartMode === 'trend';

  const series = isTrend 
    ? [
        { name: 'Repeat Rate %',      type: 'area', data: pctArr }
      ]
    : [
        { name: 'Total Patients',     type: 'bar',  data: totalPts },
        { name: 'Returning Patients', type: 'bar',  data: returningPts },
        { name: 'New Patients',       type: 'bar',  data: newPts },
        { name: 'Repeat Rate %',      type: 'line', data: pctArr },
      ];

  const chartOptions = {
    chart: {
      type: 'line',
      background: 'transparent',
      fontFamily: 'inherit',
      toolbar: { show: false },
      redrawOnParentResize: true,
      redrawOnWindowResize: true,
      animations: { enabled: true, easing: 'easeinout', speed: 800 },
      dropShadow: {
        enabled: isTrend,
        color: '#f59e0b',
        top: 3,
        left: 0,
        blur: 4,
        opacity: 0.2,
      },
    },
    colors: isTrend 
      ? ['#f59e0b'] 
      : ['#c7d9ff', '#3567D6', '#22C55E', '#f59e0b'],
    stroke: {
      width: isTrend ? [3] : [1, 1, 1, 3],
      curve: 'smooth',
      colors: isTrend ? undefined : ['#fff', '#fff', '#fff', '#f59e0b'],
    },
    fill: {
      type: isTrend ? 'gradient' : ['solid', 'solid', 'solid', 'solid'],
      gradient: isTrend ? {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.5,
        opacityFrom: 0.45,
        opacityTo: 0.0,
        stops: [0, 100],
      } : undefined,
      opacity: 1,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: 'end',
        columnWidth: '70%',
        grouped: true,
        dataLabels: { position: 'top' },
      },
    },
    markers: {
      size: isTrend ? [5] : [0, 0, 0, 5],
      hover: { size: 6 },
      strokeColors: '#fff',
      strokeWidth: 2,
    },
    dataLabels: {
      enabled: true,
      enabledOnSeries: isTrend ? [0] : [0, 1, 2, 3],
      formatter: function (val, opts) {
        if (!val && val !== 0) return '';
        const isPct = isTrend ? opts.seriesIndex === 0 : opts.seriesIndex === 3;
        if (isPct) return `${val}%`;
        return val >= 1000 ? (val / 1000).toFixed(1) + 'k' : val;
      },
      style: {
        fontSize: '10px',
        fontWeight: 700,
        // specifically color the 4th item (the percentage count inside Bar mode) orange, and normal grey for the others
        colors: isTrend ? ['#f59e0b'] : ['#475569', '#475569', '#475569', '#f59e0b']
      },
      background: {
        enabled: isTrend,
        foreColor: '#fff',
        borderRadius: 4,
        padding: 3,
        borderWidth: 0,
        dropShadow: { enabled: false }
      },
      offsetY: isTrend ? -6 : -10,
    },
    // Dual Y-axes
    yaxis: isTrend 
      ? [
          {
            seriesName: 'Repeat Rate %',
            // No strict min/max to allow it to auto-center properly and clearly read fluctuations
            title: {
              text: 'Repeat Rate (%)',
              style: { color: '#64748b', fontSize: '11px', fontWeight: 600 },
            },
            labels: {
              formatter: (v) => `${Number(v).toFixed(1)}%`,
              style: { colors: '#94a3b8', fontSize: '11px' },
            },
          }
        ]
      : [
          {
            seriesName: 'Total Patients',
            title: {
              text: 'Patient Count',
              style: { color: '#64748b', fontSize: '11px', fontWeight: 600 },
            },
            labels: {
              formatter: (v) => {
                if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
                if (v >= 1000) return (v / 1000).toFixed(0) + 'k';
                return v;
              },
              style: { colors: '#94a3b8', fontSize: '11px' },
              offsetX: -4,
            },
          },
          { seriesName: 'Returning Patients', show: false },
          { seriesName: 'New Patients',       show: false },
          {
            seriesName: 'Repeat Rate %',
            opposite: true,
            min: 0,
            max: 100,
            show: false,
          },
        ],
    xaxis: {
      categories: months,
      axisBorder: { show: false },
      axisTicks: { show: false },
      tickPlacement: 'between',
      title: {
        text: 'Month & Year',
        style: { color: '#94a3b8', fontSize: '11px', fontWeight: 600 },
      },
      labels: {
        style: { colors: '#64748b', fontSize: '11px', fontWeight: 600 },
        offsetY: 2,
      },
    },
    grid: {
      borderColor: '#f0f4ff',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 12, right: isTrend ? 30 : 12, bottom: 0, left: isTrend ? 24 : 4 },
    },
    tooltip: {
      theme: 'light',
      shared: true,
      intersect: false,
      style: { fontSize: '12px' },
      y: {
        formatter: (val, { seriesIndex }) => {
          const isPct = isTrend ? seriesIndex === 0 : seriesIndex === 3;
          return isPct ? `${val}%` : val ? val.toLocaleString('en-IN') : '0';
        },
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '11.5px',
      fontWeight: 600,
      markers: { width: 9, height: 9, radius: 3 },
      itemMargin: { horizontal: 8, vertical: 2 },
    },
  };

  // ── render ────────────────────────────────────────────────────────────────
  if (!isRedcliffe) return <EmptyState isError={false} />;
  if (loading)      return <SkeletonView />;
  if (error || !trendData) return <EmptyState isError={error} />;

  return (
    <Box
      sx={{
        border: '1px solid #e2e8f0',
        borderRadius: '14px',
        p: '14px 16px 8px',
        background: '#fafbff',
        m: '16px 16px 12px',
      }}
    >
      {/* Header row: title + toggle */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5} flexWrap="wrap" gap={1}>
        <Typography sx={{ fontWeight: 700, fontSize: '13px', color: '#170C6B' }}>
          Monthly Patient Trend
        </Typography>

        {/* Bar / Trend toggle */}
        <Stack direction="row" sx={{ background: '#F0F4FF', borderRadius: '10px', p: '3px', gap: '2px' }}>
          {[
            { key: 'trend', icon: <ShowChartIcon sx={{ fontSize: 14 }} />, label: 'Trend' },
            { key: 'bar',   icon: <BarChartIcon  sx={{ fontSize: 14 }} />, label: 'Bar'   },
          ].map(({ key, icon, label }) => (
            <Stack
              key={key}
              direction="row"
              alignItems="center"
              gap={0.4}
              onClick={() => setChartMode(key)}
              sx={{
                px: 1.25, py: 0.5,
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                background: chartMode === key ? '#3567D6' : 'transparent',
                color: chartMode === key ? '#fff' : '#64748b',
                userSelect: 'none',
              }}
            >
              {React.cloneElement(icon, {
                sx: { fontSize: 14, color: chartMode === key ? '#fff' : '#64748b' },
              })}
              <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: 'inherit' }}>
                {label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>

      <Box
        ref={chartWrapperRef}
        sx={{
          position: 'relative',
          overflowX: 'auto',
          overflowY: 'hidden',
          // removed maxWidth !important to allow scrolling
        }}
      >
        <Box sx={{ minWidth: { xs: '600px', md: '100%' } }}>
          <ReactApexChart
            key={chartMode + months.join(',')}
            options={chartOptions}
            series={series}
            type="line"
            height={230}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SmartReportRepeatRate;
